/**
 * Authentication Context Provider
 *
 * Provides authentication state and methods throughout the app
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { AuthContextType } from '../../types';
import { useAuth as useAuthHook } from '../../hooks/useAuth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider component
 * Wraps the app to provide authentication context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuthHook();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

/**
 * useAuth hook
 * Access authentication context from any component
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
